/**
 * @typedef {Object} RuleCondition
 * @property {string} field - Context dictionary'deki alan adı
 * @property {string} operator - Equals | NotEquals | GreaterThan | GreaterThanOrEqual | LessThan | LessThanOrEqual | Contains | StartsWith | EndsWith | IsNull | IsNotNull
 * @property {string} value - Karşılaştırma değeri
 */

/**
 * @typedef {Object} RuleDefinition
 * @property {RuleCondition[]} conditions - Koşul listesi
 * @property {string} logicalOperator - AND | OR
 * @property {string} successEvent - Başarı mesajı
 * @property {string} errorMessage - Hata mesajı
 */

/**
 * @typedef {Object} BusinessRule
 * @property {string} id
 * @property {string} workflowName
 * @property {string} ruleName
 * @property {string} description
 * @property {string} ruleJson - JSON string (RuleDefinition formatı)
 * @property {boolean} isActive
 * @property {number} priority
 * @property {string} category
 * @property {string} targetEntity
 * @property {string} createdBy
 * @property {string} createdDate
 */

/**
 * @typedef {Object} RuleEvaluationResult
 * @property {string} ruleName
 * @property {boolean} isSuccess
 * @property {string} message
 */

/**
 * @typedef {Object} WorkflowResult
 * @property {string} workflowName
 * @property {boolean} allRulesPassed
 * @property {RuleEvaluationResult[]} results
 */

export const OPERATORS = [
  { value: 'Equals',             label: 'Eşittir (=)' },
  { value: 'NotEquals',          label: 'Eşit Değil (≠)' },
  { value: 'GreaterThan',        label: 'Büyüktür (>)' },
  { value: 'GreaterThanOrEqual', label: 'Büyük Eşit (≥)' },
  { value: 'LessThan',           label: 'Küçüktür (<)' },
  { value: 'LessThanOrEqual',    label: 'Küçük Eşit (≤)' },
  { value: 'Contains',           label: 'İçerir' },
  { value: 'StartsWith',         label: 'Başlar' },
  { value: 'EndsWith',           label: 'Biter' },
  { value: 'IsNull',             label: 'Boş' },
  { value: 'IsNotNull',          label: 'Dolu' },
];

/** Kural yaşam döngüsü durum tanımları (#11) */
export const RULE_STATUSES = {
  0: { key: 'Draft',           label: 'Taslak',        color: '#64748b', bg: '#f8f9fa' },
  1: { key: 'Testing',         label: 'Test',          color: 'var(--accent)', bg: '#e7f1ff' },
  2: { key: 'PendingApproval', label: 'Onay Bekliyor', color: '#fd7e14', bg: '#fff3e0' },
  3: { key: 'Active',          label: 'Aktif',         color: 'var(--success)', bg: '#d1f0e0' },
  4: { key: 'Archived',        label: 'Arşiv',         color: '#adb5bd', bg: '#f1f3f5' },
};

/** Durum numarası veya anahtar adına göre durum bilgisini döndürür */
export function getStatusInfo(status) {
  if (typeof status === 'number') return RULE_STATUSES[status] ?? RULE_STATUSES[0];
  const entry = Object.entries(RULE_STATUSES).find(([, v]) => v.key === status);
  return entry ? RULE_STATUSES[parseInt(entry[0])] : RULE_STATUSES[0];
}

export const EMPTY_RULE_DEFINITION = {
  conditions: [{ field: '', operator: 'Equals', value: '' }],
  logicalOperator: 'AND',
  successEvent: '',
  errorMessage: '',
};

/** Varsayılan skorlama değerleri (#15) */
export const DEFAULT_SCORING = {
  impactScore: 5,
  easeScore: 5,
  urgencyScore: 5,
  relationScore: 5,
  impactWeight: 0.25,
  easeWeight: 0.25,
  urgencyWeight: 0.25,
  relationWeight: 0.25,
};
